import React from 'react';
import { BookOpen, Dumbbell, FileText, User, Link2 } from 'lucide-react';

// ONE DIAGRAM PER CONCEPT, each drawing that idea and nothing else (HelpPage.js).
// Flat, as Nucleus is: hairlines and tints, the concept's layer hue for whatever is
// in focus. `focus` is a part id (nucleus/concepts.js); the parts it names light,
// the rest step back, and a part's own callout appears only while it is in focus.

const W = 360;
const H = 220;

// is this element part of what is in focus? (no focus: everything is)
const tone = (focus, ...parts) => (!focus ? '' : parts.includes(focus) ? ' is-focus' : ' is-off');

// a node: the same square every node is in the identity graph, its schema's icon
// inside it (a journal entry, a workout, a document, a profile)
const SCHEMA_ICON = { journal: BookOpen, workout: Dumbbell, document: FileText, profile: User, link: Link2 };
function Node({ x, y, kind = 'journal', r = 8, className = '' }) {
    const Icon = SCHEMA_ICON[kind] || BookOpen;
    const i = r * 1.2;
    return (
        <g className={className}>
            <rect className="hd-n" x={x - r} y={y - r} width={r * 2} height={r * 2} />
            <Icon className="hd-icon" x={x - i / 2} y={y - i / 2} width={i} height={i} strokeWidth={2.2} aria-hidden="true" />
        </g>
    );
}

function Label({ x, y, children, anchor = 'middle', className = '' }) {
    return <text className={`hd-t ${className}`} x={x} y={y} textAnchor={anchor}>{children}</text>;
}

// a face: a circle with a head and shoulders
function Face({ x, y, r = 11, className = '' }) {
    return (
        <g className={`hd-face ${className}`}>
            <circle className="hd-n" cx={x} cy={y} r={r} />
            <circle className="hd-ink" cx={x} cy={y - r * 0.25} r={r * 0.3} />
            <path className="hd-stroke" d={`M${x - r * 0.55} ${y + r * 0.6}q${r * 0.55} -${r * 0.75} ${r * 1.1} 0`} />
        </g>
    );
}

// a small labelled box: an app, a plugin, an automation
function Box({ x, y, w = 58, h = 24, label, className = '' }) {
    return (
        <g className={className}>
            <rect className="hd-n" x={x} y={y} width={w} height={h} />
            <Label x={x + w / 2} y={y + h / 2 + 3.5}>{label}</Label>
        </g>
    );
}

// the same small graph, drawn in each diagram that needs your identity
const NODES = [
    { x: 92, y: 70, kind: 'journal' }, { x: 64, y: 104, kind: 'journal' }, { x: 104, y: 128, kind: 'journal' },
    { x: 176, y: 84, kind: 'workout' }, { x: 204, y: 116, kind: 'workout' }, { x: 168, y: 140, kind: 'workout' },
    { x: 124, y: 178, kind: 'document' }, { x: 182, y: 186, kind: 'document' },
];
const LINKS = [[0, 1], [1, 2], [0, 3], [2, 5], [3, 4], [4, 5], [2, 6], [5, 7], [6, 7]];
const PROFILE = { x: 140, y: 34 };
// the link between the first journal entry and the first workout, drawn as the node it is
const LINK = { x: (NODES[0].x + NODES[3].x) / 2, y: (NODES[0].y + NODES[3].y) / 2 };

function Graph({ focus, lit = {}, faded }) {
    return (
        <g className={faded ? 'is-faded' : ''}>
            {LINKS.map(([a, b]) => (
                <line key={`${a}-${b}`} className={`hd-link${tone(focus, ...((lit.link && lit.link(a, b)) || lit.links || []))}`} x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y} />
            ))}
            <line className={`hd-link${tone(focus, ...(lit.links || []))}`} x1={PROFILE.x} y1={PROFILE.y} x2={NODES[0].x} y2={NODES[0].y} />
            <line className={`hd-link${tone(focus, ...(lit.links || []))}`} x1={PROFILE.x} y1={PROFILE.y} x2={NODES[3].x} y2={NODES[3].y} />
            {NODES.map((n, i) => <Node key={i} {...n} className={tone(focus, ...((lit.node && lit.node(n, i)) || []))} />)}
            {/* your profile: a node like any other, on the profile schema */}
            <Node x={PROFILE.x} y={PROFILE.y} kind="profile" className={tone(focus, ...(lit.profile || []))} />
        </g>
    );
}

function Arrow({ x1, y1, x2, y2, className = '', dashed }) {
    const a = Math.atan2(y2 - y1, x2 - x1);
    const hx = x2 - 7 * Math.cos(a - 0.45);
    const hy = y2 - 7 * Math.sin(a - 0.45);
    const kx = x2 - 7 * Math.cos(a + 0.45);
    const ky = y2 - 7 * Math.sin(a + 0.45);
    return (
        <g className={`hd-arrow ${className}`}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} strokeDasharray={dashed ? '4 4' : undefined} />
            <path d={`M${hx} ${hy}L${x2} ${y2}L${kx} ${ky}`} />
        </g>
    );
}

// a contract: a page with a folded corner and a signature
function Contract({ x, y, s = 1, className = '' }) {
    return (
        <g className={`hd-contract ${className}`} transform={`translate(${x} ${y}) scale(${s})`}>
            <path className="hd-n" d="M0 0h22l10 10v32H0z" />
            <path className="hd-stroke" d="M22 0v10h10M6 14h14M6 20h20M6 26h16" />
            <path className="hd-sign" d="M6 35c2-4 3.5-4 4-.5.4 2.6 2 2.4 3-.3 1-2 2-2 2.6.2" />
        </g>
    );
}

/* ── IDENTITY: your graph, its nodes and schemas, your profile, the plugins ─── */
function IdentityDiagram({ focus }) {
    return (
        <>
            <Graph
                focus={focus}
                lit={{
                    links: ['graph'],
                    link: (a, b) => ['graph', ...(a === 0 && b === 3 ? ['links'] : [])],
                    node: (n, i) => ['graph', ...(i === 2 ? ['node'] : []), ...(n.kind === 'workout' ? ['schema'] : []), ...(i === 0 || i === 3 || i === 6 ? ['links'] : [])],
                    profile: ['graph', 'profile'],
                }}
            />
            {/* a link is a node of its own, on the line it draws; and a link can point at a link */}
            <g className={tone(focus, 'graph', 'links')}>
                <Node x={LINK.x} y={LINK.y} kind="link" r={7} />
            </g>
            {focus === 'links' && (
                <g className="is-focus">
                    <line className="hd-link" x1={LINK.x} y1={LINK.y + 7} x2={NODES[6].x} y2={NODES[6].y - 8} strokeDasharray="4 3" />
                    <Label x={LINK.x + 2} y={LINK.y + 20} anchor="start" className="hd-call">works on</Label>
                    <Label x={LINK.x - 4} y={150} anchor="end" className="hd-call">a link to a link</Label>
                </g>
            )}
            {/* plugins: tools that act for you in the world, from what your graph holds */}
            <g className={tone(focus, 'plugins')}>
                <Box x={262} y={48} w={84} label="send a message" />
                <Box x={262} y={84} w={84} label="place a trade" />
                <Box x={262} y={120} w={84} label="lights on" />
                <Box x={262} y={156} w={84} label="book a table" />
                <Arrow x1={214} y1={112} x2={260} y2={60} />
                <Arrow x1={214} y1={116} x2={260} y2={96} />
                <Arrow x1={214} y1={120} x2={260} y2={132} />
                <Arrow x1={214} y1={124} x2={260} y2={168} />
                {focus === 'plugins' && <Label x={304} y={198} className="hd-call">act for you</Label>}
            </g>
            {focus === 'node' && <Label x={104} y={110} className="hd-call">a node</Label>}
            {focus === 'schema' && <Label x={190} y={66} className="hd-call">workout · a schema</Label>}
            {focus === 'profile' && <Label x={154} y={37} className="hd-call" anchor="start">a node on the profile schema</Label>}
            {focus === 'graph' && <Label x={140} y={212} className="hd-call">your graph</Label>}
        </>
    );
}

/* ── REALMS: a boundary drawn around part of your identity ───────────────── */
function RealmsDiagram({ focus }) {
    const ring = { x: 40, y: 50, w: 92, h: 152 };
    if (focus === 'defaults') {
        return (
            <>
                <g>
                    <rect className="hd-ring is-focus" x={18} y={22} width={150} height={182} />
                    <g transform="translate(-36 0) scale(1)"><Graph focus="x" lit={{}} faded /></g>
                    <Label x={93} y={216} className="hd-call">Full Access: everything</Label>
                </g>
                <g>
                    <rect className="hd-ring is-focus" x={236} y={80} width={70} height={70} />
                    <Label x={271} y={168} className="hd-call">No Access: nothing</Label>
                </g>
            </>
        );
    }
    if (focus === 'one-recipient') {
        return (
            <>
                <Graph focus="x" lit={{}} faded />
                <rect className="hd-ring is-focus" x={168} y={170} width={30} height={30} />
                <Node x={182} y={186} kind="document" className="is-focus" />
                <Face x={268} y={186} r={14} className="is-focus" />
                <Arrow x1={202} y1={186} x2={250} y2={186} className="is-focus" />
                <Label x={268} y={164} className="hd-call">one recipient</Label>
                <Label x={183} y={160} className="hd-call">a realm made for them</Label>
            </>
        );
    }
    return (
        <>
            <Graph
                focus={focus}
                lit={{
                    links: [],
                    node: (n, i) => (n.kind === 'journal' ? ['schemas', 'fields'] : i === 6 ? ['nodes'] : []),
                    profile: ['profile'],
                }}
            />
            {/* the realm: a boundary around the journal and one document, by name */}
            <rect className={`hd-ring${focus ? ' is-focus' : ''}`} x={ring.x} y={ring.y} width={ring.w} height={ring.h} />
            <Label x={ring.x + 4} y={ring.y - 6} anchor="start" className="hd-ringname">Work</Label>
            {/* a node it sets aside */}
            <g className={tone(focus, 'nodes')}><path className="hd-cross" d="M98 122l12 12M110 122l-12 12" /></g>
            {/* the plugins it may run, on what it covers */}
            <g className={tone(focus, 'plugins')}>
                <Box x={268} y={110} w={64} label="plugin" />
                <Arrow x1={268} y1={122} x2={136} y2={150} dashed />
                {focus === 'plugins' && <Label x={300} y={150} className="hd-call">may run</Label>}
            </g>
            {/* the profile it shows */}
            <g className={tone(focus, 'profile')}>
                <line className="hd-link" x1={ring.x + ring.w} y1={ring.y} x2={PROFILE.x - 8} y2={PROFILE.y} />
            </g>
            {focus === 'schemas' && <Label x={86} y={44} className="hd-call">journal: read, edit</Label>}
            {focus === 'nodes' && <Label x={150} y={204} className="hd-call" anchor="start">one document, by name</Label>}
            {focus === 'profile' && <Label x={154} y={37} className="hd-call" anchor="start">the profile it shows</Label>}
            {focus === 'fields' && (
                <g className="is-focus">
                    <rect className="hd-n" x={236} y={22} width={110} height={70} />
                    <Label x={246} y={40} anchor="start">title</Label><Label x={330} y={40} anchor="end">✓</Label>
                    <Label x={246} y={58} anchor="start">mood</Label><Label x={330} y={58} anchor="end">✓</Label>
                    <Label x={246} y={76} anchor="start" className="hd-struck">body</Label><Label x={330} y={76} anchor="end">✕</Label>
                    <line className="hd-link" x1={236} y1={58} x2={100} y2={72} />
                </g>
            )}
        </>
    );
}

/* ── CONTRACTS: an entity, the realms it is shared from, and its terms ───── */
function ContractsDiagram({ focus }) {
    const statusOn = focus === 'status';
    return (
        <>
            {/* the realms it is shared from */}
            <g className={tone(focus, 'gets')}>
                <rect className="hd-ring" x={20} y={30} width={70} height={56} />
                <Label x={24} y={24} anchor="start" className="hd-ringname">Work</Label>
                <rect className="hd-ring" x={20} y={104} width={70} height={56} />
                <Label x={24} y={98} anchor="start" className="hd-ringname">Inner Circle</Label>
                <Arrow x1={92} y1={60} x2={146} y2={84} />
                <Arrow x1={92} y1={130} x2={146} y2={100} />
            </g>
            {/* the contract */}
            <g className={tone(focus, 'consent', 'history', 'gets')}>
                {focus === 'history' && <><Contract x={160} y={64} className="is-back" /><Contract x={156} y={68} className="is-back" /></>}
                <Contract x={152} y={72} />
            </g>
            {/* what it gets: what the realms cover, narrowed to what was approved */}
            <g className={tone(focus, 'gets', 'revocable')}>
                <Arrow x1={190} y1={92} x2={262} y2={92} />
                <Box x={264} y={80} w={70} label="Ledgerly" />
            </g>
            {focus === 'revocable' && <g className="is-focus"><path className="hd-cross" d="M220 80l12 24M232 80l-12 24" /><Label x={226} y={122} className="hd-call">on its next request</Label></g>}
            {focus === 'consent' && <Label x={168} y={134} className="hd-call">signed on the consent page</Label>}
            {focus === 'gets' && <Label x={226} y={76} className="hd-call">covered ∩ approved</Label>}
            {focus === 'history' && <Label x={168} y={134} className="hd-call">every change kept</Label>}
            {/* its life: active, paused, expired or revoked */}
            <g className={tone(focus, 'status')}>
                <line className="hd-time" x1={20} y1={186} x2={340} y2={186} />
                <rect className="hd-seg is-active" x={20} y={180} width={110} height={12} />
                <rect className="hd-seg is-paused" x={130} y={180} width={50} height={12} />
                <rect className="hd-seg is-active" x={180} y={180} width={90} height={12} />
                <line className="hd-stroke" x1={270} y1={174} x2={270} y2={198} />
                <Label x={75} y={210}>active</Label>
                <Label x={155} y={210}>paused</Label>
                <Label x={225} y={210}>active</Label>
                <Label x={270} y={168}>{statusOn ? 'expires or revoked' : 'ends'}</Label>
            </g>
        </>
    );
}

/* ── ENTITIES: what is outside you, reading, writing and acting ──────────── */
function EntitiesDiagram({ focus }) {
    const you = { x: 180, y: 110 };
    const at = {
        apps: { x: 40, y: 34 }, agents: { x: 40, y: 34 }, plugins: { x: 262, y: 34 },
        people: { x: 40, y: 160 }, groups: { x: 262, y: 160 }, tokens: { x: 151, y: 4 },
    };
    const line = (p, key) => (
        <g className={tone(focus, key)} key={`l-${key}`}>
            <line className="hd-link" x1={p.x + 29} y1={p.y + 12} x2={you.x} y2={you.y} />
            <Contract x={(p.x + 29 + you.x) / 2 - 8} y={(p.y + 12 + you.y) / 2 - 10} s={0.5} />
        </g>
    );
    return (
        <>
            {line(at.apps, 'apps')}{line(at.plugins, 'plugins')}{line(at.people, 'people')}{line(at.groups, 'groups')}{line(at.tokens, 'tokens')}
            {/* you: your identity */}
            <rect className="hd-you" x={you.x - 30} y={you.y - 22} width={60} height={44} />
            <Label x={you.x} y={you.y + 4}>you</Label>
            <g className={tone(focus, 'apps', 'agents')}>
                <Box x={at.apps.x} y={at.apps.y} w={58} label="app" />
                <g className={tone(focus, 'agents')}><rect className="hd-n" x={at.apps.x + 10} y={at.apps.y + 30} width={38} height={16} /><Label x={at.apps.x + 29} y={at.apps.y + 41}>agent</Label></g>
            </g>
            <g className={tone(focus, 'plugins')}><Box x={at.plugins.x} y={at.plugins.y} w={58} label="plugin" /></g>
            <g className={tone(focus, 'tokens')}><Box x={at.tokens.x} y={at.tokens.y} w={58} label="token" /></g>
            <g className={tone(focus, 'people')}><Face x={at.people.x + 14} y={at.people.y + 12} /><Face x={at.people.x + 40} y={at.people.y + 12} /></g>
            <g className={tone(focus, 'groups')}>
                <rect className="hd-n" x={at.groups.x} y={at.groups.y} width={58} height={34} />
                <rect className="hd-you is-small" x={at.groups.x + 18} y={at.groups.y + 9} width={22} height={16} />
                <Label x={at.groups.x + 29} y={at.groups.y + 48}>group</Label>
            </g>
            {focus === 'agents' && <Label x={at.apps.x} y={at.apps.y + 60} anchor="start" className="hd-call">never more than its app</Label>}
            {focus === 'groups' && <Label x={at.groups.x + 29} y={at.groups.y - 8} className="hd-call">its own identity</Label>}
            {focus && focus !== 'agents' && focus !== 'groups' && <Label x={you.x} y={you.y + 40} className="hd-call">each through a contract</Label>}
        </>
    );
}

/* ── PLUGINS: from the store to a run in a realm ────────────────────────────
   Along the top, the way a plugin comes to act: the store, Set up, its steps
   (sign in, keys, settings) and Turn on. Below, what starts a run:
   a person, an app or agent through its tool access, a tool that runs
   automatically, an automation, a flow. Every run runs in a realm: what it made lands inside it, and what
   the realm doesn't let it write is left out. */
function Clock({ x, y, r = 10 }) {
    return (
        <g>
            <circle className="hd-n" cx={x} cy={y} r={r} />
            <path className="hd-stroke" d={`M${x} ${y - r * 0.6}V${y}l${r * 0.45} ${r * 0.3}`} />
        </g>
    );
}
function Tick({ x, y, s = 1 }) {
    return <path className="hd-stroke" d={`M${x - 4 * s} ${y}l${3 * s} ${3 * s}l${6 * s} -${7 * s}`} />;
}

function PluginsDiagram({ focus }) {
    const run = { x: 156, y: 104, w: 54, h: 22 };
    const into = { x: run.x - 2, y: run.y + run.h / 2 };
    const realm = { x: 212, y: 146, w: 130, h: 62 };
    return (
        <>
            {/* the store, and setting one up from it */}
            <g className={tone(focus, 'plugin')}>
                <rect className="hd-n" x={12} y={18} width={56} height={34} />
                {[0, 1, 2].map((i) => <rect key={i} className="hd-n" x={18 + i * 16} y={24} width={12} height={12} />)}
                <Label x={40} y={47}>store</Label>
                <Arrow x1={70} y1={35} x2={94} y2={35} />
                <Label x={82} y={12}>set up</Label>
            </g>
            {/* the plugin, its tools hanging under it */}
            <g className={tone(focus, 'plugin', 'tool')}>
                <Box x={96} y={22} w={58} label="plugin" />
            </g>
            <g className={tone(focus, 'tool')}>
                {[0, 1, 2].map((i) => <rect key={i} className="hd-n" x={100 + i * 18} y={52} width={14} height={10} />)}
                <line className="hd-link" x1={125} y1={46} x2={125} y2={52} />
            </g>
            {/* setup: sign in, keys, settings */}
            <g className={tone(focus, 'setup')}>
                <Arrow x1={156} y1={35} x2={176} y2={35} />
                {['sign in', 'keys', 'settings'].map((w, i) => (
                    <g key={w}>
                        <rect className="hd-n" x={178} y={14 + i * 17} width={66} height={14} />
                        <Label x={184} y={24 + i * 17} anchor="start">{w}</Label>
                    </g>
                ))}
            </g>
            {/* turn on: your yes, on what it can do (setup's last step) */}
            <g className={tone(focus, 'setup')}>
                <Arrow x1={246} y1={35} x2={268} y2={35} />
                <Contract x={272} y={14} s={0.9} />
                <g transform="translate(300 46)"><circle className="hd-n" cx={0} cy={0} r={7} /><Tick x={0} y={0} s={0.7} /></g>
                <Label x={292} y={66}>turn on</Label>
            </g>
            {/* on, it can run */}
            <g className={tone(focus, 'setup', 'run')}>
                <Arrow x1={286} y1={72} x2={run.x + run.w + 2} y2={run.y + 6} />
            </g>
            {/* what starts a run: an app or agent through its contract, a schedule, an automation, a flow */}
            <g className={tone(focus, 'access')}>
                <Box x={12} y={80} w={50} h={20} label="app" />
                <rect className="hd-n" x={20} y={104} width={34} height={13} /><Label x={37} y={113}>agent</Label>
                <Contract x={78} y={84} s={0.5} />
                <Arrow x1={64} y1={95} x2={76} y2={95} />
                <Arrow x1={96} y1={98} x2={into.x} y2={into.y - 4} />
            </g>
            <g className={tone(focus, 'schedule')}>
                <Clock x={37} y={140} />
                <Arrow x1={50} y1={138} x2={into.x} y2={into.y} />
            </g>
            <g className={tone(focus, 'automation')}>
                <Box x={12} y={160} w={64} h={18} label="automation" />
                <Arrow x1={78} y1={166} x2={into.x} y2={into.y + 3} />
            </g>
            <g className={tone(focus, 'flow')}>
                {[0, 1, 2].map((i) => <rect key={i} className="hd-n" x={12 + i * 26} y={190} width={18} height={14} />)}
                <line className="hd-link" x1={30} y1={197} x2={38} y2={197} />
                <line className="hd-link" x1={56} y1={197} x2={64} y2={197} />
                <Label x={41} y={214}>flow</Label>
                <Arrow x1={84} y1={195} x2={into.x + 4} y2={into.y + 6} />
            </g>
            {/* the run, in a realm */}
            <g className={tone(focus, 'run', 'access', 'schedule', 'automation', 'flow')}>
                <Box x={run.x} y={run.y} w={run.w} h={run.h} label="run" />
            </g>
            <g className={tone(focus, 'run')}>
                <Arrow x1={run.x + run.w / 2} y1={run.y + run.h + 2} x2={250} y2={realm.y + 8} />
                <rect className="hd-ring" x={realm.x} y={realm.y} width={realm.w} height={realm.h} />
                <Label x={realm.x + realm.w - 4} y={realm.y - 5} anchor="end" className="hd-ringname">Everyday</Label>
                <Node x={252} y={178} kind="workout" r={7} />
                <Node x={282} y={168} kind="journal" r={7} />
                <Node x={310} y={186} kind="document" r={7} />
                <line className="hd-link" x1={259} y1={176} x2={275} y2={170} />
                {/* what the realm doesn't let it write: left out */}
                <rect className="hd-n" x={180} y={176} width={14} height={14} />
                <path className="hd-cross" d="M182 178l10 10M192 178l-10 10" />
            </g>
            {focus === 'plugin' && <Label x={40} y={66} className="hd-call">verified or not</Label>}
            {focus === 'tool' && <Label x={125} y={76} className="hd-call">what it can do</Label>}
            {focus === 'setup' && <Label x={211} y={74} className="hd-call">answered once</Label>}
            {focus === 'run' && (
                <g className="is-focus">
                    <Label x={222} y={134} anchor="start" className="hd-call">runs in a realm</Label>
                    <Label x={187} y={202} className="hd-call">left out</Label>
                </g>
            )}
            {focus === 'schedule' && <Label x={56} y={152} anchor="start" className="hd-call">runs automatically</Label>}
            {focus === 'automation' && <Label x={80} y={184} anchor="start" className="hd-call">when something changes</Label>}
            {focus === 'flow' && <Label x={84} y={212} anchor="start" className="hd-call">each step after the last</Label>}
            {focus === 'access' && (
                <g className="is-focus">
                    <Label x={12} y={72} anchor="start" className="hd-call">Allowed · Asks you · Never</Label>
                    <Label x={100} y={84} anchor="start" className="hd-call">tool access</Label>
                </g>
            )}
        </>
    );
}

const DIAGRAMS = { identity: IdentityDiagram, realms: RealmsDiagram, contracts: ContractsDiagram, entities: EntitiesDiagram, plugins: PluginsDiagram };

export default function ConceptDiagram({ concept, focus }) {
    const D = DIAGRAMS[concept];
    if (!D) return null;
    return (
        <svg className="hd" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${concept}${focus ? `: ${focus}` : ''}`} data-focus={focus || undefined}>
            <D focus={focus} />
        </svg>
    );
}
